import { useState, useRef } from 'react';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  LinkIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { evidenceApi, type Evidence } from '../api/client';
import clsx from 'clsx';

interface EvidenceUploadProps {
  disputeId: string;
  evidence: Evidence[];
  onUpdate: () => void;
}

const TYPE_CONFIG = {
  receipt_photo: { icon: DocumentIcon, label: 'Receipt', color: 'text-amber-600 bg-amber-50' },
  seal_photo: { icon: PhotoIcon, label: 'Seal Photo', color: 'text-emerald-600 bg-emerald-50' },
  cctv_link: { icon: VideoCameraIcon, label: 'CCTV', color: 'text-orange-600 bg-orange-50' },
  other: { icon: DocumentIcon, label: 'Other', color: 'text-slate-600 bg-slate-50' },
};

export default function EvidenceUpload({ disputeId, evidence, onUpdate }: EvidenceUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [selectedType, setSelectedType] = useState<string>('other');
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Allowed: JPEG, PNG, WebP, PDF');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await evidenceApi.upload(disputeId, file, selectedType);
      setSuccess('Evidence uploaded successfully');
      onUpdate();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError('Failed to upload evidence. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  }

  async function handleAddLink() {
    if (!linkUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    try {
      new URL(linkUrl);
    } catch {
      setError('Invalid URL format');
      return;
    }

    setUploading(true);
    setError('');

    try {
      await evidenceApi.addLink(disputeId, linkUrl, 'cctv_link');
      setSuccess('Evidence link added successfully');
      setShowLinkModal(false);
      setLinkUrl('');
      onUpdate();
    } catch (err) {
      setError('Failed to add evidence link. Please try again.');
      console.error('Link error:', err);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(evidenceId: string) {
    if (!confirm('Are you sure you want to delete this evidence?')) return;

    try {
      await evidenceApi.delete(disputeId, evidenceId);
      setSuccess('Evidence deleted');
      onUpdate();
    } catch (err) {
      setError('Failed to delete evidence');
      console.error('Delete error:', err);
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-4">
      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Existing Evidence */}
      {evidence.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">Uploaded Evidence</h4>
          <div className="space-y-2">
            {evidence.map((item) => {
              const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.other;
              const Icon = config.icon;

              return (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className={clsx('p-2 rounded-lg', config.color)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {item.file_name || item.external_url || config.label}
                    </p>
                    <p className="text-xs text-slate-500">
                      {item.file_size ? formatFileSize(item.file_size) : 'External link'}
                      {' • '}
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {item.external_url && (
                    <a
                      href={item.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-700">Add Evidence</h4>

        {/* Evidence Type Selector */}
        <div className="flex gap-2">
          {Object.entries(TYPE_CONFIG).map(([type, config]) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                selectedType === type
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              )}
            >
              {config.label}
            </button>
          ))}
        </div>

        {/* Upload Area */}
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={clsx(
            'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors',
            uploading
              ? 'border-amber-300 bg-amber-50'
              : 'border-slate-300 hover:border-amber-400 hover:bg-amber-50/50'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <CloudArrowUpIcon
            className={clsx(
              'w-10 h-10 mx-auto mb-2',
              uploading ? 'text-amber-500 animate-pulse' : 'text-slate-400'
            )}
          />
          <p className="text-sm text-slate-600">
            {uploading ? 'Uploading...' : 'Drop file here or click to upload'}
          </p>
          <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, PDF up to 10MB</p>
        </div>

        {/* Add Link Button */}
        <button
          onClick={() => setShowLinkModal(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <LinkIcon className="w-4 h-4" />
          Add CCTV or external link
        </button>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Evidence Link</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com/cctv-footage"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-500 mt-2">
              Add a link to CCTV footage, cloud storage, or other evidence
            </p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                }}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={uploading || !linkUrl.trim()}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
              >
                {uploading ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
